import { useState } from 'react';
import { Message } from '../../graphql/chat';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect } from 'react';

interface ChatMessageProps {
  message: Message;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const isOwnMessage = currentUserId === message.userId;

  useEffect(() => {
    fetchAuthSession().then((session) => {
      setCurrentUserId(session.tokens?.idToken?.payload.sub as string);
    });
  }, []);

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
        {/* Sender name */}
        {!isOwnMessage && (
          <span className="text-xs text-gray-400 mb-1 px-1 font-medium">{message.userName}</span>
        )}

        {/* Message bubble */}
        <div
          className={`rounded-2xl px-4 py-2 ${
            isOwnMessage
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-br-sm shadow-lg'
              : 'bg-[#3a3a3a] text-gray-100 rounded-bl-sm border border-gray-700'
          }`}
        >
          <p className="text-sm break-words whitespace-pre-wrap">{message.content}</p>
        </div>

        {/* Timestamp */}
        <span className="text-xs text-gray-500 mt-1 px-1">{formatTime(message.timestamp)}</span>

        {/* Reactions (future feature) */}
        {message.reactions && message.reactions.length > 0 && (
          <div className="flex gap-1 mt-1">
            {message.reactions.map((reaction) => (
              <span
                key={`${reaction.userId}-${reaction.emoji}`}
                className="text-xs bg-[#2c2c2c] border border-gray-700 rounded-full px-2 py-0.5"
              >
                {reaction.emoji}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
